import { useNavigate } from 'react-router';
import { ROUTES } from '@/shared/config/routes';
import { useModalState } from '@/shared/hooks';
import type { ProjectDetail } from '@/entities/project';

/**
 * Orchestrator hook for the Project Overview surface.
 *
 * Owns:
 *   - `onCreateBrief`              — navigates to the project's Briefing section.
 *   - `onAddArtifacts`             — navigates to the project's Library section
 *
 *   - `onUploadAssetsForApproval`  — navigates to the project's Approval
 *                                    section (sidebar "Approval" item, route
 *                                    `/folders`).
 *   - `editModal`                  — modal state for the Details edit affordance.
 *
 * "Invite collaborators" is NOT owned here because the share modal lives at
 * the page outlet level (`openShareModal` on `ProjectDetailOutletContext`).
 * The page wires that callback through to `QuickStart`.
 */
export function useProjectOverview(project: ProjectDetail) {
  const navigate = useNavigate();
  const editModal = useModalState();

  return {
    onCreateBrief: () => void navigate(ROUTES.PROJECT_BRIEFING(project.platformProjectId)),
    onAddArtifacts: () => void navigate(ROUTES.PROJECT_LIBRARY(project.platformProjectId)),
    // The sidebar's "Approval" item maps to PROJECT_SECTION_FOLDERS — see
    // `widgets/project-detail-sidebar/.../ProjectDetailSidebar.constants.tsx`
    // and `entities/project/model/constants.ts` (PROJECT_SECTION_LABELS).
    onUploadAssetsForApproval: () =>
      void navigate(ROUTES.PROJECT_FOLDERS(project.platformProjectId)),
    editModal: {
      isOpen: editModal.isOpen,
      isMounted: editModal.isMounted,
      open: editModal.open,
      close: editModal.close,
      instanceKey: editModal.instanceKey,
    },
  };
}
